import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SEGMENTS, ADVANTAGES, LIFECYCLE, CASE_STUDIES } from "@/data/revamp";

const EASE = [0.76, 0, 0.24, 1];

/** Two-path segment selector: GCC vs AI-native. */
export const SegmentSelector = () => (
  <section className="py-20 md:py-28 bg-[var(--bg)] border-b border-[var(--line-strong)]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <p className="overline mb-4">Who this is for</p>
      <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-[var(--ink)] mb-12 leading-tight max-w-3xl">
        Two kinds of teams.{" "}
        <span className="font-serif-accent italic text-[var(--accent)]">One partner.</span>
      </h2>
      <div className="grid md:grid-cols-2 gap-6">
        {SEGMENTS.map((s, i) => (
          <motion.div
            key={s.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.08 * i, ease: EASE }}
          >
            <Link
              to={`/${s.slug}`}
              className="group block border border-[var(--line-strong)] hover:bg-[var(--bg-2)] transition-colors p-8 md:p-12 h-full"
              data-testid={`segment-card-${s.slug}`}
            >
              <span className="font-serif-accent italic text-[var(--accent)] text-xl block mb-4">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display text-2xl md:text-4xl tracking-tight text-[var(--ink)] leading-tight mb-4">
                {s.cardTitle}
              </h3>
              <p className="text-sm md:text-base text-[var(--ink-70)] leading-relaxed mb-8">
                {s.cardBlurb}
              </p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors">
                Explore this path
                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/** Four visible advantages (of eight), each phrased with proof or approach. */
export const AdvantagesStrip = () => (
  <section className="py-20 md:py-28 bg-[var(--bg-2)] border-b border-[var(--line-strong)]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <p className="overline mb-4">Why Orphion</p>
      <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-[var(--ink)] mb-12 leading-tight max-w-3xl">
        Advantages you can{" "}
        <span className="font-serif-accent italic text-[var(--accent)]">verify</span>.
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {ADVANTAGES.map((a, i) => (
          <motion.div
            key={a.k}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.06 * i, ease: EASE }}
            className="border border-[var(--line-strong)] bg-[var(--bg)] p-7"
          >
            <span className="font-serif-accent italic text-[var(--accent)] text-lg block mb-4">{a.k}</span>
            <h3 className="font-display text-xl tracking-tight text-[var(--ink)] mb-3 leading-snug">
              {a.title}
            </h3>
            <p className="text-sm text-[var(--ink-70)] leading-relaxed">{a.body}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.22em] text-[var(--ink-50)]">
        <span>+ Fractional CHRO on demand</span>
        <span>+ Placement guarantee</span>
        <span>+ 14+ yrs · Miratech · Quantiphi · Capita · SHRM-SCP</span>
        <span>+ Free 30-min diagnostic call</span>
      </div>
    </div>
  </section>
);

/** Hire → Onboard → Operate → Scale lifecycle graphic. */
export const LifecycleStrip = () => (
  <section className="py-20 md:py-28 bg-[var(--bg)] border-b border-[var(--line-strong)]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <p className="overline mb-4">Full lifecycle</p>
      <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-[var(--ink)] mb-14 leading-tight max-w-3xl">
        Hire → Onboard → Operate →{" "}
        <span className="font-serif-accent italic text-[var(--accent)]">Scale</span>.
      </h2>
      <div className="grid md:grid-cols-4 gap-0 border-t border-l border-[var(--line-strong)]">
        {LIFECYCLE.map((l, i) => (
          <motion.div
            key={l.stage}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.08 * i, ease: EASE }}
            className="border-b border-r border-[var(--line-strong)] p-7 md:p-8 relative"
          >
            <span className="font-display text-5xl md:text-6xl tracking-tight text-[var(--ink)]/[0.10] absolute top-4 right-6 select-none">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="font-display text-2xl tracking-tight text-[var(--ink)] mb-3">{l.stage}</h3>
            <p className="text-sm text-[var(--ink-70)] leading-relaxed mb-5">{l.body}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">{l.services}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/** Case-study proof cards (drafts until client-verified). */
export const CaseStudiesTeaser = () => (
  <section className="py-20 md:py-28 bg-[var(--bg-2)] border-b border-[var(--line-strong)]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <p className="overline mb-4">Proof</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-[var(--ink)] leading-tight">
            The number is{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">the story</span>.
          </h2>
        </div>
        <Link
          to="/case-studies"
          className="link-slide text-xs uppercase tracking-[0.22em] text-[var(--ink)] hover:text-[var(--accent)]"
          data-testid="case-studies-more"
        >
          All case studies →
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {CASE_STUDIES.map((c, i) => (
          <motion.div
            key={c.slug}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.06 * i, ease: EASE }}
          >
            <Link
              to={`/case-studies/${c.slug}`}
              className="group block border border-[var(--line-strong)] bg-[var(--bg)] hover:border-[var(--accent)] transition-colors p-8 md:p-10 h-full"
              data-testid={`home-case-${c.slug}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1">
                  {c.segment}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] border border-[var(--line-strong)] px-2 py-1">
                  Draft — pending verification
                </span>
              </div>
              <h3 className="font-display text-xl md:text-2xl tracking-tight text-[var(--ink)] leading-snug mb-4">
                {c.title}
              </h3>
              <p className="font-serif-accent italic text-[var(--accent)] text-lg">{c.metric}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
